import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLock,
  faCrown,
  faCheck,
  faCreditCard,
  faPlus,
  faMinus,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../AuthContext';
import PremiumLayout from './PremiumLayout';
import PremiumSteps from './PremiumSteps';
import { formatRoDate } from './formatRoDate';
import { formatLeiExact, estimateExpiry } from './money';
import { useEnsureProfile } from './useEnsureProfile';
import { stripeAppearance } from './stripeAppearance';
import CountyCombobox from './CountyCombobox';
import { DEFAULT_COUNTRY, countryOptions, resolveCounty } from './address';

const apiUrl = process.env.REACT_APP_API_URL;
const GENERIC_ERROR = 'A apărut o eroare. Încearcă din nou.';

async function getJson(path) {
  const res = await fetch(`${apiUrl}${path}`, { credentials: 'include' });
  if (!res.ok) throw new Error(path);
  return res.json();
}

async function postJson(path, body) {
  const res = await fetch(`${apiUrl}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || data.message || GENERIC_ERROR);
  return data;
}

// loadStripe o singură dată per cheie (altfel Elements se remontează la fiecare render).
const stripeLoaders = new Map();
const getStripe = (publishableKey) => {
  if (!stripeLoaders.has(publishableKey)) stripeLoaders.set(publishableKey, loadStripe(publishableKey));
  return stripeLoaders.get(publishableKey);
};

const EMPTY_FORM = {
  nume: '',
  prenume: '',
  email: '',
  tara: DEFAULT_COUNTRY,
  judet: '',
  adresa: '',
  firma: '',
  cui: '',
  termeni: false,
};
const ADDRESS_MAX = 250; // la fel ca limita de pe server

// Aceeași regulă ca <input type="email"> (definiția WHATWG): butonul se aprinde exact
// când și browserul ar accepta adresa.
const EMAIL_RE =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

const joinRo = (items) =>
  items.length > 1 ? `${items.slice(0, -1).join(', ')} și ${items[items.length - 1]}` : items[0];

/* =====================================================
   Formularul (prezentare). Montat O SINGURĂ DATĂ — doar `paymentSlot`
   se schimbă când Stripe termină de încărcat, ca să nu se piardă ce a tastat utilizatorul.
===================================================== */
const CheckoutForm = ({ plan, user, expiry, paymentSlot, paymentComplete, onSubmit, submitting, error, disabledReason }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [showCompany, setShowCompany] = useState(false);
  const [showBenefits, setShowBenefits] = useState(false);

  useEffect(() => {
    if (user?.email) setForm((f) => (f.email ? f : { ...f, email: user.email }));
  }, [user]);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };
  // Alt stat → județul ales nu mai are sens.
  const onCountryChange = (e) => {
    const tara = e.target.value;
    setForm((f) => ({ ...f, tara, judet: '' }));
  };
  const inRomania = form.tara === DEFAULT_COUNTRY;
  const judet = inRomania ? resolveCounty(form.judet) : form.judet.trim();

  const price = plan ? formatLeiExact(plan.priceBani) : '—';
  const months = plan?.durationMonths || 1;
  const canPay = Boolean(plan && onSubmit && !disabledReason && !submitting);
  // Butonul rămâne apăsabil (validarea browserului arată ce lipsește), dar se „aprinde" doar
  // când totul e completat — ca butonul „Verifică" din lecții.
  const missing = [
    !form.nume.trim() && 'numele',
    !form.prenume.trim() && 'prenumele',
    !EMAIL_RE.test(form.email.trim()) && 'un email valid',
    inRomania && !judet && 'județul',
    !form.adresa.trim() && 'adresa',
    !paymentComplete && 'datele cardului',
    !form.termeni && 'acceptarea termenilor',
  ].filter(Boolean);
  const ready = canPay && missing.length === 0;
  const showHint = canPay && !ready && !error;

  return (
    <form
      className="pm-co"
      onSubmit={(e) => {
        e.preventDefault();
        if (canPay) onSubmit({ ...form, judet: judet || '', adresa: form.adresa.trim() });
      }}
    >
      <div className="pm-co-main">
        <section className="pm-card" aria-labelledby="pm-billing-title">
          <h2 id="pm-billing-title" className="pm-co-title">Date de facturare</h2>
          <div className="pm-co-grid">
            <div className="pm-field">
              <label htmlFor="pm-nume">Nume<span className="pm-req" aria-hidden="true">*</span></label>
              <input id="pm-nume" name="nume" className="pm-input" value={form.nume} onChange={onChange} required autoComplete="family-name" />
            </div>
            <div className="pm-field">
              <label htmlFor="pm-prenume">Prenume<span className="pm-req" aria-hidden="true">*</span></label>
              <input id="pm-prenume" name="prenume" className="pm-input" value={form.prenume} onChange={onChange} required autoComplete="given-name" />
            </div>
            <div className="pm-field pm-field--full">
              <label htmlFor="pm-email">Email<span className="pm-req" aria-hidden="true">*</span></label>
              <input id="pm-email" name="email" type="email" className="pm-input" value={form.email} onChange={onChange} required autoComplete="email" />
              <p className="pm-help">Email pentru confirmarea comenzii, chitanță și factură.</p>
            </div>
            <div className="pm-field">
              <label htmlFor="pm-tara">Țară<span className="pm-req" aria-hidden="true">*</span></label>
              <select
                id="pm-tara"
                name="tara"
                className="pm-input pm-select"
                value={form.tara}
                onChange={onCountryChange}
                required
                autoComplete="country"
              >
                {countryOptions().map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="pm-field">
              {inRomania ? (
                <>
                  <label htmlFor="pm-judet">Județ<span className="pm-req" aria-hidden="true">*</span></label>
                  <CountyCombobox
                    id="pm-judet"
                    name="judet"
                    value={form.judet}
                    onChange={(value) => setForm((f) => ({ ...f, judet: value }))}
                    required
                  />
                </>
              ) : (
                <>
                  <label htmlFor="pm-judet">Regiune / stat</label>
                  <input
                    id="pm-judet"
                    name="judet"
                    className="pm-input"
                    value={form.judet}
                    onChange={onChange}
                    maxLength={120}
                    autoComplete="address-level1"
                  />
                </>
              )}
            </div>
            <div className="pm-field pm-field--full">
              <label htmlFor="pm-adresa">Adresă<span className="pm-req" aria-hidden="true">*</span></label>
              <textarea
                id="pm-adresa"
                name="adresa"
                className="pm-input pm-textarea"
                rows={2}
                value={form.adresa}
                onChange={onChange}
                required
                maxLength={ADDRESS_MAX}
                autoComplete="street-address"
                aria-describedby="pm-adresa-help"
              />
              <p id="pm-adresa-help" className="pm-help">
                Localitatea, strada, numărul și, dacă e cazul, blocul, scara și apartamentul.
              </p>
            </div>
            {showCompany && (
              <>
                <div className="pm-field">
                  <label htmlFor="pm-firma">Denumire firmă</label>
                  <input id="pm-firma" name="firma" className="pm-input" value={form.firma} onChange={onChange} autoComplete="organization" />
                </div>
                <div className="pm-field">
                  <label htmlFor="pm-cui">CUI</label>
                  <input id="pm-cui" name="cui" className="pm-input" value={form.cui} onChange={onChange} />
                </div>
              </>
            )}
          </div>
          <button type="button" className="pm-linkbtn" onClick={() => setShowCompany((v) => !v)} aria-expanded={showCompany}>
            <FontAwesomeIcon icon={showCompany ? faMinus : faPlus} aria-hidden="true" />
            {showCompany ? 'Renunță la datele firmei' : 'Adaugă datele firmei (factură pe firmă)'}
          </button>
        </section>

        <section className="pm-card" aria-labelledby="pm-pay-title">
          <h2 id="pm-pay-title" className="pm-co-title">Metodă de plată</h2>
          <div className="pm-pay-option">
            <input type="radio" id="pm-pay-card" name="metoda" checked readOnly />
            <label htmlFor="pm-pay-card">
              <FontAwesomeIcon icon={faCreditCard} aria-hidden="true" />
              Card bancar
            </label>
            <span className="pm-brands" aria-label="Carduri acceptate">
              <span className="pm-brand">VISA</span>
              <span className="pm-brand">MASTERCARD</span>
            </span>
          </div>
          <div className="pm-pay-element">{paymentSlot}</div>
          <label className="pm-check">
            <input type="checkbox" name="termeni" checked={form.termeni} onChange={onChange} required />
            <span>
              Am citit și accept <Link to="/termeni">Termenii și condițiile</Link> și{' '}
              <Link to="/politica">Politica de confidențialitate</Link>.
            </span>
          </label>
          <p className="pm-privacy">
            <FontAwesomeIcon icon={faLock} className="pm-privacy-icon" aria-hidden="true" />
            <span>
              Datele cardului sunt procesate securizat de Stripe; ContaLearn nu vede și nu stochează numărul cardului.
              Datele de facturare le folosim doar pentru comandă și factură.
            </span>
          </p>
        </section>
      </div>

      <aside className="pm-co-aside">
        <section className="pm-card" aria-labelledby="pm-sum-title">
          <h2 id="pm-sum-title" className="pm-sum-title">Sumar comandă</h2>
          <div className="pm-sum-item">
            <span className="pm-sum-item-name">
              <FontAwesomeIcon icon={faCrown} aria-hidden="true" />
              <span>
                {plan?.name || 'ContaLearn Premium'}
                <small className="pm-sum-item-sub">Acces {months === 1 ? '1 luna' : `${months} luni`}</small>
              </span>
            </span>
            <button type="button" className="pm-linkbtn" style={{ marginTop: 0 }} onClick={() => setShowBenefits((v) => !v)} aria-expanded={showBenefits}>
              Detalii
            </button>
          </div>
          {showBenefits && plan && (
            <ul className="pm-sum-benefits">
              {plan.benefits.map((benefit) => (
                <li key={benefit}>
                  <FontAwesomeIcon icon={faCheck} aria-hidden="true" />
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
          )}
          <div className="pm-sum-row pm-sum-row--total">
            <span>Total de plată</span>
            <span>{price}</span>
          </div>
          {expiry && <p className="pm-sum-note">Premium activ până la {expiry}</p>}
          <div className="pm-co-actions">
            {(error || disabledReason) && (
              <p className="pm-error" role="alert">
                {error || disabledReason}
              </p>
            )}
            <button
              type="submit"
              className={`pm-btn pm-btn-primary pm-btn-block pm-pay-btn${ready || submitting ? ' is-ready' : ''}`}
              disabled={!canPay}
              aria-describedby={showHint ? 'pm-pay-hint' : undefined}
            >
              <FontAwesomeIcon icon={faLock} aria-hidden="true" />
              {submitting ? 'Se procesează…' : `Plătește ${plan ? price : ''}`}
            </button>
            {showHint && (
              <p id="pm-pay-hint" className="pm-pay-hint">
                De completat: {joinRo(missing)}.
              </p>
            )}
            <p className="pm-note">Accesul Premium se activează imediat după confirmarea plății. Fără reînnoire automată.</p>
          </div>
        </section>
      </aside>
    </form>
  );
};

/* =====================================================
   În interiorul <Elements>: doar PaymentElement. Hook-urile Stripe trăiesc aici și
   expun {stripe, elements} părintelui printr-un callback — părintele rămâne montat.
===================================================== */
// Țara se alege în „Date de facturare"; Stripe nu o mai cere a doua oară și o primește la confirmare.
const PAYMENT_ELEMENT_OPTIONS = {
  layout: 'tabs',
  fields: { billingDetails: { address: { country: 'never' } } },
};

const StripePaymentSection = ({ onApi, onReady, onComplete }) => {
  const stripe = useStripe();
  const elements = useElements();
  useEffect(() => {
    onApi(stripe && elements ? { stripe, elements } : null);
    return () => onApi(null);
  }, [stripe, elements, onApi]);
  return (
    <PaymentElement options={PAYMENT_ELEMENT_OPTIONS} onReady={onReady} onChange={(event) => onComplete(event.complete)} />
  );
};

/* =====================================================
   Pagina: încarcă config + plan, montează Elements în slotul de plată (deferred intent —
   comanda și PaymentIntent-ul se creează abia la „Plătește"), confirmă și verifică pe server.
===================================================== */
const PremiumCheckout = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedPlanId = searchParams.get('plan');
  const auth = useAuth();
  const user = auth?.user;
  useEnsureProfile(auth);

  const [config, setConfig] = useState(null);
  const [plan, setPlan] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [elementReady, setElementReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [apiReady, setApiReady] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const payApiRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([getJson('/premium/config'), getJson('/premium/plans')])
      .then(([cfg, data]) => {
        if (cancelled) return;
        setConfig(cfg);
        const plans = data.plans || [];
        // `?plan=` vine de pe pagina de planuri; un id necunoscut cade pe primul plan (lunar).
        setPlan(plans.find((p) => p.id === requestedPlanId) || plans[0] || null);
      })
      .catch(() => {
        if (!cancelled) setLoadError('Oferta nu a putut fi încărcată. Încearcă din nou.');
      });
    return () => {
      cancelled = true;
    };
  }, [requestedPlanId]);

  const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark-mode');
  const stripePromise = useMemo(
    () => (config?.enabled && config.publishableKey ? getStripe(config.publishableKey) : null),
    [config]
  );
  const elementsOptions = useMemo(
    () =>
      plan
        ? {
            mode: 'payment',
            amount: plan.priceBani,
            currency: String(plan.currency || 'RON').toLowerCase(),
            appearance: stripeAppearance(isDark),
            locale: 'ro',
          }
        : null,
    [plan, isDark]
  );

  const handleApi = useCallback((api) => {
    payApiRef.current = api;
    setApiReady(Boolean(api));
  }, []);
  const handleReady = useCallback(() => setElementReady(true), []);

  const handlePay = async (form) => {
    const api = payApiRef.current;
    if (!api || !plan) {
      setError('Formularul de plată nu este încă pregătit. Încearcă din nou în câteva secunde.');
      return;
    }
    const { stripe, elements } = api;
    setError('');
    setSubmitting(true);
    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(submitError.message || GENERIC_ERROR);
        return;
      }

      const { termeni, ...billing } = form;
      const order = await postJson('/premium/checkout', { planId: plan.id, billing });

      const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret: order.clientSecret,
        redirect: 'if_required',
        confirmParams: {
          return_url: `${window.location.origin}/premium/succes?order=${order.orderId}`,
          payment_method_data: {
            billing_details: {
              name: `${form.prenume} ${form.nume}`.trim(),
              email: form.email,
              address: { country: form.tara },
            },
          },
        },
      });
      if (confirmError) {
        setError(confirmError.message || GENERIC_ERROR);
        // O reîncercare creează o comandă nouă; pe aceasta o marcăm eșuată prin verificarea de pe server.
        postJson(`/premium/orders/${order.orderId}/sync`).catch(() => {});
        return;
      }

      if (paymentIntent && paymentIntent.status === 'succeeded') {
        // Serverul verifică la Stripe și activează; nu ne bazăm pe ce spune browserul.
        const result = await postJson(`/premium/orders/${order.orderId}/sync`);
        if (auth?.checkAuth) await auth.checkAuth();
        navigate('/premium/succes', {
          replace: true,
          state: { orderId: order.orderId, status: result.status, expiresAt: result.premium_expires_at, planName: plan.name },
        });
        return;
      }
      // processing / requires_action fără redirect: pagina de succes verifică până se decide.
      navigate(`/premium/succes?order=${order.orderId}`, { replace: true });
    } catch (err) {
      setError(err.message || GENERIC_ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  const expiry = formatRoDate(estimateExpiry(user?.premium_expires_at, plan?.durationDays || 30));
  const paymentsUnavailable = Boolean(config && !config.enabled);
  const fallbackReason =
    loadError || (paymentsUnavailable ? 'Plățile nu sunt disponibile momentan. Încearcă mai târziu.' : '');
  const stripeMounted = Boolean(stripePromise && elementsOptions);

  const paymentSlot = (
    <>
      {!(stripeMounted && elementReady) && (
        <div className="pm-pay-slot" aria-live="polite">
          <FontAwesomeIcon icon={faLock} className="pm-pay-slot-icon" aria-hidden="true" />
          <span>{fallbackReason || 'Se încarcă formularul securizat de plată…'}</span>
        </div>
      )}
      {stripeMounted && (
        <div hidden={!elementReady}>
          <Elements stripe={stripePromise} options={elementsOptions}>
            <StripePaymentSection onApi={handleApi} onReady={handleReady} onComplete={setPaymentComplete} />
          </Elements>
        </div>
      )}
    </>
  );

  return (
    <PremiumLayout variant="checkout" backTo="/premium/plans" backLabel="Înapoi la planuri">
      <div className="pm-strip" role="status">
        <FontAwesomeIcon icon={faCrown} className="pm-strip-icon" aria-hidden="true" />
        <span>
          Premium îți deblochează toate cursurile și vieți nelimitate imediat după plată.{' '}
          <Link to="/premium/plans">Vezi beneficiile</Link>
        </span>
      </div>

      <PremiumSteps current={1} />

      <CheckoutForm
        plan={plan}
        user={user}
        expiry={expiry}
        paymentSlot={paymentSlot}
        paymentComplete={paymentComplete}
        onSubmit={stripeMounted && elementReady && apiReady ? handlePay : null}
        submitting={submitting}
        error={error}
        disabledReason={fallbackReason}
      />
    </PremiumLayout>
  );
};

export default PremiumCheckout;
